import React, { useState } from 'react';
import NativeDatePickerModal from 'react-native-modal-datetime-picker';
import WebDatePicker from 'react-mobile-datepicker';
import moment from 'moment';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useController } from 'react-hook-form';

import { FormGroupDatePickerProps } from '@nocode/types/formGroup.type';
import {
  dateConfig,
  dateTimeConfig,
} from '@common/components/DatePicker/helps/config';
import { isCanvas } from '@common/utils';

const DATE_FORMATS = {
  date: 'YYYY/MM/DD',
  datetime: 'YYYY/MM/DD hh:mm',
  time: 'hh:mm',
};

const FormGroupDatePicker = ({
  name,
  mode = 'date',
  styleConfig,
}: FormGroupDatePickerProps) => {
  const style = createStyle(styleConfig);
  const canvas = isCanvas() && Platform.OS === 'web';

  const {
    field,
    fieldState: { invalid, isTouched, isDirty },
    formState: { touchedFields, dirtyFields },
  } = useController({
    name,
  });

  const [isDatePickerVisible, setDatePickerVisibility] = useState(false);

  const events = {
    onShowDatePicker: () => {
      setDatePickerVisibility(true);
    },
    onHideDatePicker: () => {
      setDatePickerVisibility(false);
    },
    onConfirmChangeDate: (date: any) => {
      field.onChange(date.toISOString());
      events.onHideDatePicker();
    },
  };

  const displayDate = field.value
    ? moment(field.value).format(DATE_FORMATS[mode])
    : moment(Date.now()).format(DATE_FORMATS[mode]);

  if (canvas) {
    return (
      <View style={style.trigger}>
        <Text style={style.triggerText}>{displayDate}</Text>
      </View>
    );
  }

  if (Platform.OS === 'web') {
    return (
      <>
        <Pressable
          style={({ pressed }) => [
            style.trigger,
            {
              opacity: pressed ? 0.5 : 1,
            },
          ]}
          onPress={events.onShowDatePicker}
        >
          <Text style={style.triggerText}>{displayDate}</Text>
        </Pressable>
        <WebDatePicker
          min={new Date(1900, 0, 0)}
          max={new Date(2200, 0, 0)}
          isOpen={isDatePickerVisible}
          onCancel={events.onHideDatePicker}
          onSelect={events.onConfirmChangeDate}
          dateConfig={mode === 'date' ? dateConfig : dateTimeConfig}
        />
      </>
    );
  }

  return (
    <>
      <Pressable
        style={({ pressed }) => [
          style.trigger,
          {
            opacity: pressed ? 0.5 : 1,
          },
        ]}
        onPress={events.onShowDatePicker}
      >
        <Text style={style.triggerText}>{displayDate}</Text>
      </Pressable>
      <NativeDatePickerModal
        minimumDate={new Date(1900, 0, 0)}
        maximumDate={new Date(2200, 0, 0)}
        isVisible={isDatePickerVisible}
        mode={mode}
        onConfirm={events.onConfirmChangeDate}
        onCancel={events.onHideDatePicker}
      />
    </>
  );
};

const createStyle = (style: FormGroupDatePickerProps['styleConfig']) =>
  StyleSheet.create({
    trigger: {
      borderColor: style?.borderColor || '#E9EFF2',
      backgroundColor: style?.backgroundColor,
      borderWidth: 1,
      borderRadius: 4,
      paddingVertical: 12,
      width: style?.size?.width || '100%',
    },
    triggerText: {
      textAlign: 'center',
      color: style?.color,
      fontSize: style?.fontSize || 16,
    },
  });

export default FormGroupDatePicker;
